/**
 * Operator directory: search, filters, invite entry points, navigation to `UserDetails`.
 *
 * Where: `/users`.
 * Uses: `teamApi` user listing, URL params, infinite scroll or pagination as implemented.
 * Behavior: staff-gated; supports impersonation entry from rows where enabled.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Snackbar,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  LinkedIn as LinkedInIcon,
  Visibility as VisibilityIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { teamApi } from '@/api/team';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { InviteUserModal } from '@/components/user';
import type { TeamUserResponse, TeamUser } from '@/types/team';
import type { InfiniteData } from '@tanstack/react-query';
import type { LinkedInConnectionStatus } from '@/api/user';

// Custom impersonate icon component
const ImpersonateIcon = ({ fontSize = 'small' }: { fontSize?: 'small' | 'medium' | 'large' }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <PersonIcon fontSize={fontSize} />
    <ArrowForwardIcon 
      fontSize={fontSize} 
      sx={{ 
        position: 'absolute', 
        left: -4, 
        top: '50%', 
        transform: 'translateY(-50%)',
        fontSize: fontSize === 'small' ? '0.7rem' : fontSize === 'medium' ? '0.9rem' : '1.1rem'
      }} 
    />
  </Box>
);

// Helper function to determine LinkedIn status based on user data
const getLinkedInStatus = (user: TeamUser): LinkedInConnectionStatus => {
  // If user has unipile_id, they have a LinkedIn account
  const hasAccount = !!user.unipile_id;
  
  // Use the new is_unipile_connected field to determine connection status
  const isConnected = !!user.is_unipile_connected;
  
  return {
    is_connected: isConnected,
    has_account: hasAccount,
  };
};

// Helper function to get LinkedIn status display props
const getLinkedInStatusDisplay = (status: LinkedInConnectionStatus) => {
  if (status.is_connected) {
    return {
      label: 'Connected',
      color: 'success' as const,
      icon: <LinkedInIcon />,
    };
  } else if (status.has_account) {
    return {
      label: 'Disconnected',
      color: 'error' as const,
      icon: <LinkedInIcon />,
    };
  } else {
    return {
      label: 'Not Connected',
      color: 'default' as const,
      icon: <LinkedInIcon />,
    };
  }
}

// Helper function to format premium feature names
const formatPremiumFeature = (feature: string): string => {
  const featureMap: Record<string, string> = {
    sales_navigator: 'Sales Navigator',
    recruiter: 'Recruiter',
    premium: 'Premium',
  };
  return featureMap[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Helper function to get premium feature color
const getPremiumFeatureColor = (feature: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  const colorMap: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
    sales_navigator: 'primary',
    recruiter: 'secondary',
    premium: 'success',
  };
  return colorMap[feature] || 'default';
}

interface UsersProps {}

type StatusFilterValue = 'ALL' | 'ACTIVE' | 'SUSPENDED' | 'DELETED';
type LinkedinFilterValue = 'all' | 'connected' | 'disconnected' | 'no_account';
type PremiumFilterValue = 'all' | 'sales_navigator' | 'recruiter' | 'premium';

export const Users: React.FC<UsersProps> = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize searchTerm from URL params
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  
  // Debounced search term for URL updates (to avoid too many URL changes)
  const searchTermDebounced = useDebouncedValue(searchTerm, 500);
  
  // Track if we're updating from internal state to avoid circular updates
  const isInternalUpdateRef = useRef(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Allow all authenticated users to access the page (they can at least invite users)
  // The backend will return empty list if they don't have access to see other users

  // Filters
  // Default: only ACTIVE users, other filters default to "all"
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus === 'ALL' || urlStatus === 'ACTIVE' || urlStatus === 'SUSPENDED' || urlStatus === 'DELETED') {
      return urlStatus;
    }
    return 'ACTIVE';
  });

  const [linkedinFilter, setLinkedinFilter] = useState<LinkedinFilterValue>(() => {
    const urlLinkedin = searchParams.get('linkedin');
    if (urlLinkedin === 'connected' || urlLinkedin === 'disconnected' || urlLinkedin === 'no_account') {
      return urlLinkedin;
    }
    return 'all';
  });

  const [premiumFilter, setPremiumFilter] = useState<PremiumFilterValue>(() => {
    const urlPremium = searchParams.get('premium_feature');
    if (urlPremium === 'sales_navigator' || urlPremium === 'recruiter' || urlPremium === 'premium') {
      return urlPremium;
    }
    return 'all';
  });

  // Fetch users with infinite scroll
  // Include user key in query key to ensure proper data refresh on impersonation
  const {
    data: usersPages,
    isLoading: isLoadingUsers,
    error: usersError,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetchingNextPage: isFetchingNextUsersPage,
  } = useInfiniteQuery<
    TeamUserResponse,
    Error,
    InfiniteData<TeamUserResponse, string | undefined>,
    [string, string | undefined, string],
    string | undefined
  >({
    queryKey: ['team-users', currentUser?.key, searchTermDebounced] as [string, string | undefined, string],
    queryFn: ({ pageParam }) =>
      teamApi.getUsers({
        page_token: pageParam,
        max_results: 10,
        search: searchTermDebounced || undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.output.next_page_token,
    initialPageParam: undefined,
  });

  // Flatten users from all pages
  const users: TeamUser[] = usersPages?.pages.flatMap((page: TeamUserResponse) => page.output.items) ?? [];

  // Apply client-side filters on the loaded users
  const filteredUsers: TeamUser[] = users.filter((user) => {
    // Status filter (default: ACTIVE)
    if (statusFilter !== 'ALL' && user.status !== statusFilter) {
      return false;
    }

    // LinkedIn connection status filter
    if (linkedinFilter !== 'all') {
      const linkedInStatus = getLinkedInStatus(user);
      if (linkedinFilter === 'connected' && !linkedInStatus.is_connected) {
        return false;
      }
      if (
        linkedinFilter === 'disconnected' &&
        !(linkedInStatus.has_account && !linkedInStatus.is_connected)
      ) {
        return false;
      }
      if (linkedinFilter === 'no_account' && linkedInStatus.has_account) {
        return false;
      }
    }

    // Premium features filter
    if (premiumFilter !== 'all') {
      if (!Array.isArray(user.premium_features) || !user.premium_features.includes(premiumFilter)) {
        return false;
      }
    }

    return true;
  });

  // Update URL params when filters/search change
  const updateURLParams = useCallback((
    updates: {
      search?: string;
      status?: StatusFilterValue;
      linkedin?: LinkedinFilterValue;
      premium_feature?: PremiumFilterValue;
    }
  ) => {
    isInternalUpdateRef.current = true;
    const newSearchParams = new URLSearchParams(searchParams);
    
    if (updates.search !== undefined) {
      if (updates.search) {
        newSearchParams.set('search', updates.search);
      } else {
        newSearchParams.delete('search');
      }
    }

    if (updates.status !== undefined) {
      // Only persist status if it's not the default ACTIVE
      if (updates.status && updates.status !== 'ACTIVE') {
        newSearchParams.set('status', updates.status);
      } else {
        newSearchParams.delete('status');
      }
    }

    if (updates.linkedin !== undefined) {
      // Only persist linkedin filter if it's not the default "all"
      if (updates.linkedin && updates.linkedin !== 'all') {
        newSearchParams.set('linkedin', updates.linkedin);
      } else {
        newSearchParams.delete('linkedin');
      }
    }

    if (updates.premium_feature !== undefined) {
      // Only persist premium filter if it's not the default "all"
      if (updates.premium_feature && updates.premium_feature !== 'all') {
        newSearchParams.set('premium_feature', updates.premium_feature);
      } else {
        newSearchParams.delete('premium_feature');
      }
    }
    
    setSearchParams(newSearchParams, { replace: true });
    // Reset flag after a short delay to allow URL to update
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 0);
  }, [searchParams, setSearchParams]);

  // Sync state from URL params when URL changes externally (e.g., browser back button)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return; // Skip if this is from our own internal update
    }
    const urlSearch = searchParams.get('search') || '';
    const urlStatus = searchParams.get('status');
    const urlLinkedin = searchParams.get('linkedin');
    const urlPremium = searchParams.get('premium_feature');

    // Only update state if URL values differ from current state
    if (urlSearch !== searchTerm) {
      setSearchTerm(urlSearch);
    }
    const nextStatus: StatusFilterValue =
      urlStatus === 'ALL' || urlStatus === 'ACTIVE' || urlStatus === 'SUSPENDED' || urlStatus === 'DELETED'
        ? urlStatus
        : 'ACTIVE';
    if (nextStatus !== statusFilter) {
      setStatusFilter(nextStatus);
    }

    const nextLinkedin: LinkedinFilterValue =
      urlLinkedin === 'connected' || urlLinkedin === 'disconnected' || urlLinkedin === 'no_account'
        ? urlLinkedin
        : 'all';
    if (nextLinkedin !== linkedinFilter) {
      setLinkedinFilter(nextLinkedin);
    }

    const nextPremium: PremiumFilterValue =
      urlPremium === 'sales_navigator' || urlPremium === 'recruiter' || urlPremium === 'premium'
        ? urlPremium
        : 'all';
    if (nextPremium !== premiumFilter) {
      setPremiumFilter(nextPremium);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update URL when debounced search term or filters change
  useEffect(() => {
    updateURLParams({
      search: searchTermDebounced,
      status: statusFilter,
      linkedin: linkedinFilter,
      premium_feature: premiumFilter,
    });
  }, [searchTermDebounced, statusFilter, linkedinFilter, premiumFilter, updateURLParams]);

  // Intersection observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextUsersPage && !isFetchingNextUsersPage) {
          fetchNextUsersPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextUsersPage, isFetchingNextUsersPage, fetchNextUsersPage]);

  // Impersonation mutation
  const impersonationMutation = useMutation({
    mutationFn: teamApi.startImpersonation,
    onSuccess: () => {
      setToast({
        open: true,
        message: 'Impersonation started successfully',
        severity: 'success',
      });
      // Refresh the page to show impersonation status
      window.location.reload();
    },
    onError: (error: any) => {
      setToast({
        open: true,
        message: error.response?.data?.error_messages?.[0] || 'Failed to start impersonation',
        severity: 'error',
      });
    },
  });


  const handleUserSelect = (user: TeamUser) => {
    // Preserve all filters/search params when navigating to user detail page
    const query = searchParams.toString();
    const url = query ? `/users/${user.key}?${query}` : `/users/${user.key}`;
    navigate(url);
  };

  const handleImpersonate = (userUuid: string) => {
    impersonationMutation.mutate({ user_uuid: userUuid });
  };

  const getRoleLabel = (user: TeamUser) => {
    if (user.is_superuser) return 'Superuser';
    if (user.is_staff) return 'Staff';
    return 'User';
  };

  const getRoleColor = (user: TeamUser) => {
    if (user.is_superuser) return 'error';
    if (user.is_staff) return 'warning';
    return 'info';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'SUSPENDED': return 'warning';
      case 'DELETED': return 'error';
      default: return 'default';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const created = new Date(dateString);
    const diffInMonths = (now.getFullYear() - created.getFullYear()) * 12 + 
                         (now.getMonth() - created.getMonth());
    
    if (diffInMonths === 0) return 'Less than a month ago';
    if (diffInMonths === 1) return '1 month ago';
    return `${diffInMonths} months ago`;
  };

  // Allow all authenticated users to access the page (they can at least invite users)
  // The backend will return empty list if they don't have access to see other users

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              Users
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage users and their permissions.
            </Typography>
          </Box>
          
          {!isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowInviteModal(true)}
            >
              Invite User
            </Button>
          )}
        </Box>

        {/* Search and Invite Button Row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => {
              isInternalUpdateRef.current = true;
              setSearchTerm(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: isMobile ? '100%' : 400 }}
          />
          
          {/* Filters */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              flex: 1,
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilterValue)}
              >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
                <MenuItem value="DELETED">Deleted</MenuItem>
                <MenuItem value="ALL">All Statuses</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="linkedin-filter-label">LinkedIn</InputLabel>
              <Select
                labelId="linkedin-filter-label"
                label="LinkedIn"
                value={linkedinFilter}
                onChange={(e) => setLinkedinFilter(e.target.value as LinkedinFilterValue)}
              >
                <MenuItem value="all">All Connections</MenuItem>
                <MenuItem value="connected">Connected</MenuItem>
                <MenuItem value="disconnected">Disconnected</MenuItem>
                <MenuItem value="no_account">No Account</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="premium-filter-label">Premium Features</InputLabel>
              <Select
                labelId="premium-filter-label"
                label="Premium Features"
                value={premiumFilter}
                onChange={(e) => setPremiumFilter(e.target.value as PremiumFilterValue)}
              >
                <MenuItem value="all">All Features</MenuItem>
                <MenuItem value="sales_navigator">Sales Navigator</MenuItem>
                <MenuItem value="recruiter">Recruiter</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {isMobile && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowInviteModal(true)}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Invite
            </Button>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {isLoadingUsers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : usersError ? (
          <Alert severity="error">
            Failed to load users. Please try again.
          </Alert>
        ) : users.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No users to manage
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You don't have access to view other users, but you can still invite new users to teams.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowInviteModal(true)}
            >
              Invite User
            </Button>
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No users match the selected filters
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try changing or clearing some of the filters to see more users.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Desktop Table View */}
            {!isMobile && (
              <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>LinkedIn</TableCell>
                      <TableCell>Unipile ID</TableCell>
                      <TableCell>Premium Features</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.map((user: TeamUser) => (
                      <TableRow
                        key={user.key || user.uuid || `user-${Math.random()}`}
                        hover
                        onClick={() => handleUserSelect(user)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={user.profile_image || undefined}
                              sx={{ width: 40, height: 40 }}
                            >
                              {(user.name && user.name.charAt(0).toUpperCase()) || (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {user.name || user.email || 'User'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {user.email || ''}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={getRoleLabel(user)}
                                  color={getRoleColor(user) as any}
                                  size="small"
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {user.created_at ? getTimeAgo(user.created_at) : ''}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.status}
                            color={getStatusColor(user.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const linkedInStatus = getLinkedInStatus(user);
                            const displayProps = getLinkedInStatusDisplay(linkedInStatus);
                            return (
                              <Chip
                                label={displayProps.label}
                                color={displayProps.color}
                                size="small"
                                icon={displayProps.icon}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {user.unipile_id || 'Not set'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(() => {
                              const linkedInStatus = getLinkedInStatus(user);
                              const canShowPremium =
                                linkedInStatus.is_connected &&
                                Array.isArray(user.premium_features) &&
                                user.premium_features.length > 0;

                              if (!canShowPremium) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    None
                                  </Typography>
                                );
                              }

                              return user.premium_features!.map((feature) => (
                                <Chip
                                  key={feature}
                                  label={formatPremiumFeature(feature)}
                                  color={getPremiumFeatureColor(feature)}
                                  size="small"
                                />
                              ));
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small">
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {user.key !== currentUser?.key && (
                              <Tooltip title="Sign in as this user">
                                <IconButton 
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleImpersonate(user.key);
                                  }}
                                >
                                  <ImpersonateIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Mobile List View */}
            {isMobile && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredUsers.map((user: TeamUser, index: number) => (
                  <Card
                    key={user.key || user.uuid || `user-${index}`}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => handleUserSelect(user)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      {/* Row A: Avatar + Name + Impersonate Icon */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={user.profile_image || undefined}
                            sx={{ width: 40, height: 40 }}
                          >
                            {(user.name && user.name.charAt(0).toUpperCase()) || (user.email ? user.email.charAt(0).toUpperCase() : 'U')}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                            {user.name || user.email || 'User'}
                          </Typography>
                        </Box>
                        {user.key !== currentUser?.key && (
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImpersonate(user.key);
                            }}
                          >
                            <Tooltip title="Sign in as this user">
                              <ImpersonateIcon />
                            </Tooltip>
                          </IconButton>
                        )}
                      </Box>

                      {/* Row B: Status and LinkedIn Status */}
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={user.status}
                          color={getStatusColor(user.status) as any}
                          size="small"
                          variant="outlined"
                        />
                        {(() => {
                          const linkedInStatus = getLinkedInStatus(user);
                          const displayProps = getLinkedInStatusDisplay(linkedInStatus);
                          return (
                            <Chip
                              label={displayProps.label}
                              color={displayProps.color}
                              size="small"
                              icon={displayProps.icon}
                            />
                          );
                        })()}
                      </Box>

                      {/* Premium Features Row - only show when LinkedIn is connected, same logic as details page */}
                      {(() => {
                        const linkedInStatus = getLinkedInStatus(user);
                        const canShowPremium =
                          linkedInStatus.is_connected &&
                          Array.isArray(user.premium_features) &&
                          user.premium_features.length > 0;

                        if (!canShowPremium) {
                          return null;
                        }

                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {user.premium_features!.map((feature) => (
                              <Chip
                                key={feature}
                                label={formatPremiumFeature(feature)}
                                color={getPremiumFeatureColor(feature)}
                                size="small"
                              />
                            ))}
                          </Box>
                        );
                      })()}

                      {/* Row C: Email Address */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                        {user.email || ''}
                      </Typography>

                      {/* Row D: Last Active */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {user.created_at ? getTimeAgo(user.created_at) : ''}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Loading indicator for infinite scroll */}
            {isFetchingNextUsersPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Load more trigger */}
            <div ref={loadMoreRef} style={{ height: 20 }} />
          </>
        )}
      </Box>

      {/* Invite User Modal */}
      <InviteUserModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={(message) =>
          setToast({
            open: true,
            message: message || 'Invitation sent successfully',
            severity: 'success',
          })
        }
      />

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast(prev => ({ ...prev, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
